import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Form, Button, Alert, Spinner } from 'react-bootstrap';
import { FaDownload, FaExclamationTriangle, FaInfoCircle } from 'react-icons/fa';
import { toast } from 'react-toastify';
import { useAuth } from '../../../services/hooks/useAuth';
import { useProyectos } from '../../../services/hooks/useProyectos';
import { useComedores } from '../../../services/hooks/useComedores';
import { useTipoServiciosAlimentacion } from '../../../services/hooks/useTipoServiciosAlimentacion';
import { downloadExcelReport } from '../../../services/utils/downloadHelper';
import { reporteApi } from '../../../services/api/reporteApi';

const SistemaOfflineContingencia = () => {
  const { user: currentUser } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  
  // Formulario
  const [formData, setFormData] = useState({
    id_proyecto: '',
    id_comedor: '',
    id_tipo_servicio: ''
  });

  // Hooks para obtener datos
  const { comedores, fetchComedoresByProyecto, loading: loadingComedores } = useComedores();
  const { tipoServiciosAlimentacion, fetchTipoServiciosAlimentacion, loading: loadingTiposServicio } = useTipoServiciosAlimentacion();
  
  // Obtener proyecto seleccionado de la sesión
  const selectedProject = JSON.parse(localStorage.getItem('selectedProject') || '{}');

  // Cargar comedores y tipos de servicio del proyecto seleccionado
  useEffect(() => {
    console.log('Selected project en useEffect:', selectedProject);
    if (selectedProject && selectedProject.id) {
      console.log('Cargando comedores para proyecto:', selectedProject.id);
      fetchComedoresByProyecto(selectedProject.id);
      // Cargar tipos de servicio de alimentación
      fetchTipoServiciosAlimentacion();
      // Establecer el proyecto en el formulario
      setFormData(prev => ({
        ...prev,
        id_proyecto: selectedProject.id
      }));
    }
  }, [selectedProject.id]);

  // Manejar cambios en el formulario
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // El proyecto ya está establecido desde selectedProject, no necesitamos cambiarlo
  };

  // Generar Excel offline
  const handleGenerarExcel = async (e) => {
    e.preventDefault();
    
    console.log('Form data:', formData);
    console.log('Selected project:', selectedProject);
    
    if (!formData.id_proyecto || !formData.id_comedor || !formData.id_tipo_servicio) {
      toast.error('Por favor complete todos los campos requeridos (Comedor y Tipo de Servicio)');
      return;
    }

    setIsLoading(true);
    try {
      const params = {
        id_proyecto: formData.id_proyecto,
        id_comedor: formData.id_comedor,
        id_tipo_servicio: formData.id_tipo_servicio
      };
      
      console.log('Params enviados:', params);

      const result = await downloadExcelReport(
        reporteApi.generarExcelOfflineContingencia,
        params,
        'Sistema_Offline_Contingencia.xlsx'
      );

      console.log('Resultado descarga:', result);
      toast.success(`Excel offline generado exitosamente: ${result.filename}`);
    } catch (error) {
      console.error('Error al generar Excel offline:', error);
      console.error('Error response:', error.response);
      console.error('Error data:', error.response?.data);
      
      let errorMessage = 'Error al generar el Excel offline';
      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Container fluid className="py-4">
      <Row>
        <Col>
          <Card className="shadow-sm">
            <Card.Header className="bg-primary text-white">
              <div className="d-flex align-items-center">
                <FaExclamationTriangle className="me-2" />
                <h5 className="mb-0">Sistema Offline - Contingencia Comedores</h5>
              </div>
            </Card.Header>
            
            <Card.Body>
              {/* Información importante */}
              <Alert variant="warning" className="mb-4">
                <FaInfoCircle className="me-2" />
                <strong>Sistema de Contingencia:</strong> Use esta opción cuando el sistema principal no esté disponible. 
                Genera un archivo Excel con macros VBA para capturar comensales offline con lector de código de barras.
              </Alert>

              <Alert variant="info" className="mb-4">
                <strong>Funcionalidades del Excel offline:</strong>
                <ul className="mb-0 mt-2">
                  <li>Captura automática con lector de código de barras</li>
                  <li>Validación de comensales autorizados para el comedor</li>
                  <li>Selección de tipo de servicio en el formulario VBA</li>
                  <li>Prevención de registros duplicados</li>
                  <li>Interfaz similar al sistema online</li>
                  <li>Timestamp automático de cada registro</li>
                </ul>
              </Alert>

              <Form onSubmit={handleGenerarExcel}>
                {/* Proyecto - Arriba */}
                <Row>
                  <Col md={12}>
                    <Form.Group className="mb-3">
                      <Form.Label>Proyecto (automático)</Form.Label>
                      <Form.Control
                        type="text"
                        value={selectedProject?.nombre || 'No hay proyecto seleccionado'}
                        disabled
                        readOnly
                      />
                      <small className="text-muted">Proyecto de la sesión actual</small>
                    </Form.Group>
                  </Col>
                </Row>

                {/* Comedor y Tipo de Servicio - Abajo */}
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Comedor <span className="text-danger">*</span></Form.Label>
                      <Form.Select
                        name="id_comedor"
                        value={formData.id_comedor}
                        onChange={handleInputChange}
                        disabled={loadingComedores}
                        required
                      >
                        <option value="">Seleccione un comedor</option>
                        {comedores && comedores.map(comedor => (
                          <option key={comedor.id} value={comedor.id}>
                            {comedor.nombre}
                          </option>
                        ))}
                      </Form.Select>
                      {loadingComedores && <small className="text-muted">Cargando comedores...</small>}
                    </Form.Group>
                  </Col>

                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Tipo de Servicio <span className="text-danger">*</span></Form.Label>
                      <Form.Select
                        name="id_tipo_servicio"
                        value={formData.id_tipo_servicio}
                        onChange={handleInputChange}
                        disabled={loadingTiposServicio}
                        required
                      >
                        <option value="">Seleccione un tipo de servicio</option>
                        {tipoServiciosAlimentacion && tipoServiciosAlimentacion.map(tipo => (
                          <option key={tipo.id} value={tipo.id}>
                            {tipo.descripcion}
                          </option>
                        ))}
                      </Form.Select>
                      {loadingTiposServicio && <small className="text-muted">Cargando tipos de servicio...</small>}
                    </Form.Group>
                  </Col>
                </Row>

                <div className="d-flex justify-content-end gap-2">
                  <Button
                    type="submit"
                    variant="success"
                    disabled={isLoading || !formData.id_proyecto || !formData.id_comedor || !formData.id_tipo_servicio}
                    className="d-flex align-items-center"
                  >
                    {isLoading ? (
                      <>
                        <Spinner size="sm" className="me-2" />
                        Generando Excel...
                      </>
                    ) : (
                      <>
                        <FaDownload className="me-2" />
                        Generar Excel Offline
                      </>
                    )}
                  </Button>
                </div>
              </Form>

              {/* Instrucciones de uso */}
              <Alert variant="secondary" className="mt-4">
                <h6><strong>Instrucciones de uso:</strong></h6>
                <ol>
                  <li><strong>Genere el Excel:</strong> Complete el formulario y descargue el archivo Excel</li>
                  <li><strong>Configure Excel:</strong> Abra el archivo, habilite macros y siga las instrucciones de la hoja "CodigoVBA"</li>
                  <li><strong>Capture datos:</strong> Use la hoja "Formulario" con su lector de código de barras</li>
                  <li><strong>Sincronización:</strong> Cuando el sistema esté disponible, use la opción de carga masiva para procesar los datos</li>
                </ol>
                <p className="mb-0">
                  <strong>Nota:</strong> El Excel contiene todas las hojas necesarias: Configuración, Comensales autorizados, 
                  Formulario de captura, Turnos registrados y Código VBA completo.
                </p>
              </Alert>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default SistemaOfflineContingencia;